/* ============================================================
   Турниры MLBB · квалификации MCA League (QNEA).

   Табы: Обзор / Регистрация / Команды / Сетка.
   Механика живёт на бэкенде (/api/fed-teams, см. fed/mlbb/store.js);
   при недоступном бэке показываем ошибку и не имитируем успешную регистрацию.
   ============================================================ */
import { useEffect, useRef, useState } from 'react';
import { useAppStore } from '../../store';
import { useFedStore } from '../store';
import { MLBB_RULES_RU, MLBB_RULES_KZ, teamRegistrationOpen, registrationPending, registrationOpensLabel } from '../data';
import { Ic } from '../icons';
import { Rv, Field, Pill } from '../ui';
import { MATCH_CENTER_PUBLIC_ENABLED } from '../features';
import { useMlbb, userKeyOf, myTeamsOf, regTeamIdsOf, TEAM_MAIN, TEAM_MAX } from './store';
import MlbbBracket from './Bracket';
import MatchCenter from '../matches/MatchCenter';
import NovaBracket from './NovaBracket';

// Фирменные цвета турнира (под афишу MLBB)
export const VIO = {
  accent: 'var(--mlbb-accent,#A855F7)',
  bright: 'var(--mlbb-bright,#C084FC)',
  deep: 'var(--mlbb-deep,#6D28D9)',
  glow: 'var(--mlbb-glow,rgba(168,85,247,.4))',
  soft: 'var(--mlbb-soft,rgba(168,85,247,.12))',
  line: 'var(--mlbb-line,rgba(168,85,247,.35))',
};

const Q2 = {
  accent: '#FF1638',
  bright: '#FF3651',
  deep: '#A5001D',
  glow: 'rgba(255,22,56,.48)',
  soft: 'rgba(255,22,56,.12)',
  line: 'rgba(255,54,81,.38)',
};

const CS2 = {
  accent: '#F03A22', bright: '#FF6A4D', deep: '#9E1510',
  glow: 'rgba(255,67,34,.48)', soft: 'rgba(255,67,34,.12)', line: 'rgba(255,91,61,.4)',
};

const CS2_COPY = {
  ru: {
    ov_prize: 'Призовой фонд турнира', ov_slots: 'команд', ov_cta: 'Зарегистрировать команду',
    chips: ['5×5 · LAN · BO1', 'Открытый турнир', 'Петропавловск'], qual_pill: 'NOVA CS2 OPEN',
    ki_regclose: 'Регистрация до', ki_date: 'Дата турнира', ki_place: 'Формат', ki_format: 'Подано заявок', ki_prize: 'Призовой фонд',
    champ_slots: 'Чемпион NOVA CS2 OPEN', steps_t: 'Регистрация команды',
    step2: 'Профиль игрока CS2', step2_d: 'Steam ID, игровой никнейм и Telegram нужны для состава и связи.',
    step4_d: 'Капитан собирает минимум 5 игроков и подтверждает участие команды.',
    prof_t: 'Профиль игрока CS2', prof_gameid: 'Steam ID', prof_hint: 'Укажите Steam ID или ссылку на профиль Steam.',
    reg_done: 'Команда зарегистрирована на NOVA CS2 OPEN!',
    reg_done_d: 'Заявка команды принята. Список участников уже доступен во вкладке «Команды», а сетка появится во время турнира.',
    reg_open_bracket: 'Перейти к командам', teams_sub: 'Все созданные команды NOVA CS2 OPEN — от первого игрока до готового состава.',
    teams_empty: 'Пока нет созданных команд — соберите состав первыми!',
    org_default: 'Федерация киберспорта СКО',
    team_copied: 'Ссылка приглашения скопирована',
  },
  kz: {
    ov_prize: 'Турнирдің жүлде қоры', ov_slots: 'команда', ov_cta: 'Команданы тіркеу',
    chips: ['5×5 · LAN · BO1', 'Ашық турнир', 'Петропавл'], qual_pill: 'NOVA CS2 OPEN',
    ki_regclose: 'Тіркелу дейін', ki_date: 'Турнир күні', ki_place: 'Формат', ki_format: 'Өтінім берілді', ki_prize: 'Жүлде қоры',
    champ_slots: 'NOVA CS2 OPEN чемпионы', steps_t: 'Команданы тіркеу',
    step2: 'CS2 ойыншы профилі', step2_d: 'Steam ID, ойын никнеймі және Telegram құрам мен байланыс үшін қажет.',
    step4_d: 'Капитан кемінде 5 ойыншы жинап, команданың қатысуын растайды.',
    prof_t: 'CS2 ойыншы профилі', prof_gameid: 'Steam ID', prof_hint: 'Steam ID немесе Steam профиліне сілтемені көрсетіңіз.',
    reg_done: 'Команда NOVA CS2 OPEN турниріне тіркелді!',
    reg_done_d: 'Команда өтінімі қабылданды. Қатысушылар «Командалар» бөлімінде көрінеді, ал тор турнир кезінде пайда болады.',
    reg_open_bracket: 'Командаларға өту', teams_sub: 'NOVA CS2 OPEN үшін құрылған барлық командалар — бір ойыншыдан дайын құрамға дейін.',
    teams_empty: 'Әзірге құрылған команда жоқ — құрамды бірінші болып жинаңыз!',
    org_default: 'СҚО киберспорт федерациясы',
    team_copied: 'Шақыру сілтемесі көшірілді',
  },
};

const DOTA_COPY = {
  ru: {
    ...CS2_COPY.ru,
    chips: ['5×5 · LAN', 'Открытый турнир', 'Петропавловск'], qual_pill: 'NOVA DOTA 2 OPEN',
    champ_slots: 'Чемпион NOVA DOTA 2 OPEN', step2: 'Профиль игрока Dota 2',
    step2_d: 'Укажите игровой ID Dota 2, никнейм и Telegram для связи.',
    prof_t: 'Профиль игрока Dota 2', prof_gameid: 'ID игрока Dota 2',
    prof_hint: 'Числовой ID можно найти в профиле игрока в Dota 2.',
    reg_done: 'Команда зарегистрирована на NOVA DOTA 2 OPEN!',
    teams_t: 'Команды турнира', teams_sub: 'Собирайте состав по коду приглашения. Для подачи заявки нужны пять основных игроков.',
  },
  kz: {
    ...CS2_COPY.kz,
    chips: ['5×5 · LAN', 'Ашық турнир', 'Петропавл'], qual_pill: 'NOVA DOTA 2 OPEN',
    champ_slots: 'NOVA DOTA 2 OPEN чемпионы', step2: 'Dota 2 ойыншы профилі',
    step2_d: 'Dota 2 ойыншы ID-ін, никнейм мен байланыс үшін Telegram көрсетіңіз.',
    prof_t: 'Dota 2 ойыншы профилі', prof_gameid: 'Dota 2 ойыншы ID-і',
    prof_hint: 'Сандық ID-ді Dota 2 ойынындағы ойыншы профилінен табуға болады.',
    reg_done: 'Команда NOVA DOTA 2 OPEN турниріне тіркелді!',
    teams_t: 'Турнир командалары', teams_sub: 'Шақыру коды арқылы құрам жинаңыз. Өтінім беру үшін бес негізгі ойыншы қажет.',
  },
};

const MDICT = {
  ru: {
    tab_overview: 'Обзор', tab_matches: 'Матчи', tab_reg: 'Регистрация', tab_teams: 'Команды', tab_bracket: 'Сетка',
    ov_about: 'О турнире', ov_rules: 'Регламент', ov_prize: 'Призовой фонд MCA League', ov_slots: 'слота в лигу',
    ov_key: 'Ключевая информация', ov_post: 'Пост о турнире в Telegram', ov_cta: 'Зарегистрировать команду', ov_find: 'Найти команду по коду',
    ov_org: 'Организатор',
    chips: ['5×5 · Single Elim', 'Только команды из РК', 'Онлайн · KZ'],
    qual_pill: 'Квалификации #',
    ki_regclose: 'Регистрация до', ki_date: 'Старт матчей', ki_place: 'Формат', ki_format: 'Слотов в лигу', ki_prize: 'Призы', ki_players: 'Команд',
    st_reg_open: 'Регистрация открыта',
    st_done: 'Квалификации завершены',
    champ_t: 'Чемпион квалификаций',
    champ_slots: '2 слота в первом сезоне MCA League',
    champ_runnerup: 'Второе место',
    steps_t: 'Как попасть на турнир',
    step1: 'Аккаунт на сайте', step1_d: 'Зарегистрируйся на сайте федерации — заявки и чек-ин привязаны к аккаунту.',
    step2: 'Профиль игрока', step2_d: 'Игровой ID, никнейм и Telegram — эти данные понадобятся для матчей.',
    step3: 'Команда 5×5', step3_d: 'Создай свою команду или вступи по коду приглашения.',
    step4: 'Заявка на турнир', step4_d: 'Капитан выбирает команду и подаёт заявку — команда попадает в сетку.',
    acc_done: 'Аккаунт есть — вы вошли как', acc_need: 'Сначала нужен аккаунт на сайте',
    acc_need_d: 'Регистрация занимает минуту. После неё вернётесь прямо сюда.',
    acc_signup: 'Создать аккаунт', acc_login: 'У меня есть аккаунт',
    prof_t: 'Профиль игрока MLBB', prof_saved: 'Профиль сохранён',
    prof_gameid: 'Игровой ID', prof_nick: 'Никнейм в игре', prof_tg: 'Telegram',
    prof_save: 'Сохранить профиль', prof_hint: 'ID можно посмотреть в игре: профиль → под аватаром.',
    team_t: 'Твоя команда',
    team_none: 'Ты пока не состоишь в команде.',
    team_create_t: 'Создать команду', team_name: 'Название команды', team_tag: 'Тег (до 5 симв.)',
    team_create: 'Создать', team_join_t: 'Вступить по коду', team_code: 'Код приглашения', team_join: 'Вступить',
    team_invite: 'Код приглашения', team_copy: 'Скопировать', team_copied: 'Код скопирован',
    team_cap: 'капитан', team_you: 'ты', team_slot: 'свободный слот',
    team_main: 'Основной состав', team_subs: 'Замена', team_subs_hint: 'по желанию · до 2 игроков',
    team_sub_slot: 'слот замены', team_optional: 'по желанию',
    team_fill_demo: '+ Тестовые игроки (демо)', team_leave: 'Покинуть команду', team_disband: 'Удалить команду',
    team_kick: 'Убрать',
    team_have_one: 'У тебя уже есть команда. На турнир можно заявить одну команду — чтобы создать или вступить в другую, сначала удали текущую или покинь её.',
    team_del_confirm: 'Удалить команду «{name}»? Действие необратимо.',
    team_leave_confirm: 'Покинуть команду «{name}»?',
    reg_submit: 'Подать заявку на турнир', reg_done: 'Команда зарегистрирована на квалификации!',
    reg_done_d: 'Вы в сетке. Идёт регистрация — как только она закроется, в сетке появятся соперники и стартуют матчи. Загляните во вкладку «Сетка», чтобы увидеть свой путь.',
    reg_open_bracket: 'Открыть сетку', reg_cancel: 'Отозвать заявку',
    reg_need5: 'Для заявки нужен основной состав — минимум 5 игроков',
    reg_only_cap: 'Заявку подаёт капитан команды',
    reg_accepted: 'Заявка принята',
    reg_closed_pill: 'Регистрация закрыта',
    reg_closed_d: 'Приём заявок на турнир завершён. Уже поданные заявки остаются в силе.',
    reg_locked: 'Матчи уже начались — заявку не отозвать: сетка пересеется и сыгранные матчи собьются. По составу пишите организаторам.',
    team_locked: 'Матчи уже начались — состав команды зафиксирован. Для изменений свяжитесь с организатором.',
    team_reg_closed: 'Регистрация закрыта — состав принятой команды зафиксирован. Для замены игрока свяжитесь с организатором.',
    err_notfound: 'Команда с таким кодом не найдена', err_full: 'Состав заполнен — уже 7 игроков (5 основы + 2 замены)',
    err_already: 'Ты уже в этой команде', err_noprofile: 'Сначала заполни профиль игрока', err_offline: 'Нет связи с сервером — данные не сохранены',
    err_generic: 'Не получилось — попробуй ещё раз',
    teams_t: 'Зарегистрированные команды', teams_sub: 'Команды, которые уже в сетке турнира.',
    teams_members: 'состав', teams_empty: 'Пока ни одной команды — подай заявку первым!',
    fill_all: 'Заполни все поля',
  },
  kz: {
    tab_overview: 'Шолу', tab_matches: 'Матчтар', tab_reg: 'Тіркелу', tab_teams: 'Командалар', tab_bracket: 'Тор',
    ov_about: 'Турнир туралы', ov_rules: 'Регламент', ov_prize: 'MCA League жүлде қоры', ov_slots: 'лигаға слот',
    ov_key: 'Негізгі ақпарат', ov_post: 'Telegram-дағы турнир посты', ov_cta: 'Команданы тіркеу', ov_find: 'Код арқылы команда табу',
    ov_org: 'Ұйымдастырушы',
    chips: ['5×5 · Single Elim', 'Тек ҚР командалары', 'Онлайн · KZ'],
    qual_pill: 'Іріктеу #',
    ki_regclose: 'Тіркелу дейін', ki_date: 'Матч басталуы', ki_place: 'Формат', ki_format: 'Лигаға слот', ki_prize: 'Жүлде', ki_players: 'Команда',
    st_reg_open: 'Тіркелу ашық',
    st_done: 'Іріктеу аяқталды',
    champ_t: 'Іріктеу чемпионы',
    champ_slots: 'MCA League бірінші маусымына 2 слот',
    champ_runnerup: 'Екінші орын',
    steps_t: 'Турнирге қалай қатысуға болады',
    step1: 'Сайттағы аккаунт', step1_d: 'Федерация сайтында тіркел — өтінімдер мен чек-ин аккаунтқа байланған.',
    step2: 'Ойыншы профилі', step2_d: 'Ойын ID, никнейм және Telegram — бұл деректер матчтарға қажет.',
    step3: '5×5 команда', step3_d: 'Өз командаңды құр немесе шақыру коды арқылы қосыл.',
    step4: 'Турнирге өтінім', step4_d: 'Капитан команданы таңдап, өтінім береді — команда торға түседі.',
    acc_done: 'Аккаунт бар — сіз кірдіңіз', acc_need: 'Алдымен сайтта аккаунт керек',
    acc_need_d: 'Тіркелу бір минут алады. Одан кейін дәл осында ораласыз.',
    acc_signup: 'Аккаунт құру', acc_login: 'Аккаунтым бар',
    prof_t: 'MLBB ойыншы профилі', prof_saved: 'Профиль сақталды',
    prof_gameid: 'Ойын ID', prof_nick: 'Ойындағы никнейм', prof_tg: 'Telegram',
    prof_save: 'Профильді сақтау', prof_hint: 'ID ойында көрінеді: профиль → аватар астында.',
    team_t: 'Сенің командаң',
    team_none: 'Сен әзірге командада жоқсың.',
    team_create_t: 'Команда құру', team_name: 'Команда атауы', team_tag: 'Тег (5 таңбаға дейін)',
    team_create: 'Құру', team_join_t: 'Код арқылы қосылу', team_code: 'Шақыру коды', team_join: 'Қосылу',
    team_invite: 'Шақыру коды', team_copy: 'Көшіру', team_copied: 'Код көшірілді',
    team_cap: 'капитан', team_you: 'сен', team_slot: 'бос орын',
    team_main: 'Негізгі құрам', team_subs: 'Ауыстыру', team_subs_hint: 'қалау бойынша · 2 ойыншыға дейін',
    team_sub_slot: 'ауыстыру орны', team_optional: 'қалау бойынша',
    team_fill_demo: '+ Тест ойыншылар (демо)', team_leave: 'Командадан шығу', team_disband: 'Команданы жою',
    team_kick: 'Шығару',
    team_have_one: 'Сенде команда бар. Турнирге бір команда тіркеуге болады — басқасын құру немесе қосылу үшін алдымен ағымдағыны жой не одан шық.',
    team_del_confirm: '«{name}» командасын жою керек пе? Әрекет қайтарылмайды.',
    team_leave_confirm: '«{name}» командасынан шығасың ба?',
    reg_submit: 'Турнирге өтінім беру', reg_done: 'Команда іріктеуге тіркелді!',
    reg_done_d: 'Сіз тордасыз. Тіркелу жүріп жатыр — ол жабылған соң торда қарсыластар пайда болып, матчтар басталады. Өз жолыңды көру үшін «Тор» бөліміне кіріңіз.',
    reg_open_bracket: 'Торды ашу', reg_cancel: 'Өтінімді қайтарып алу',
    reg_need5: 'Өтінімге негізгі құрам керек — кемінде 5 ойыншы',
    reg_only_cap: 'Өтінімді команда капитаны береді',
    reg_accepted: 'Өтінім қабылданды',
    reg_closed_pill: 'Тіркелу жабық',
    reg_closed_d: 'Турнирге өтінім қабылдау аяқталды. Берілген өтінімдер күшінде қалады.',
    reg_locked: 'Матчтар басталып кетті — өтінімді қайтарып алуға болмайды: тор қайта таралып, ойналған матчтар бұзылады. Құрам бойынша ұйымдастырушыларға жазыңыз.',
    team_locked: 'Матчтар басталды — команда құрамы бекітілді. Өзгерістер үшін ұйымдастырушыға жазыңыз.',
    team_reg_closed: 'Тіркелу жабық — қабылданған команданың құрамы бекітілді. Ойыншыны ауыстыру үшін ұйымдастырушыға жазыңыз.',
    err_notfound: 'Мұндай кодпен команда табылмады', err_full: 'Құрам толды — 7 ойыншы бар (5 негізгі + 2 ауыстыру)',
    err_already: 'Сен бұл командадасың', err_noprofile: 'Алдымен ойыншы профилін толтыр', err_offline: 'Сервермен байланыс жоқ — деректер сақталмады',
    err_generic: 'Сәтсіз аяқталды — қайталап көр',
    teams_t: 'Тіркелген командалар', teams_sub: 'Турнир торындағы командалар.',
    teams_members: 'құрам', teams_empty: 'Әзірге бірде-бір команда жоқ — бірінші бол!',
    fill_all: 'Барлық өрісті толтыр',
  },
};

const card = { background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))' };

function StepBadge({ n, done }) {
  return (
    <span
      className="flex-shrink-0 flex items-center justify-center font-display font-bold rounded-[9px]"
      style={{
        width: 34, height: 34, fontSize: 16,
        background: done ? 'rgba(67,217,106,.14)' : `linear-gradient(140deg,${VIO.accent},${VIO.deep})`,
        border: done ? '1px solid rgba(67,217,106,.4)' : 'none',
        color: done ? 'var(--fed-ok)' : '#fff',
        boxShadow: done ? 'none' : `0 6px 16px -6px ${VIO.glow}`,
      }}
    >
      {done ? <Ic name="check" size={16} sw={2.6} /> : n}
    </span>
  );
}

/** Карточка команды: состав 5 слотов, код приглашения, действия.
    locked — матчи турнира уже идут: отзыв заявки и роспуск команды пересеяли бы
    сетку, поэтому кнопки убраны (жёсткий запрет — на бэке, _tour_started). */
function TeamCard({ team, M, userKey, isReged, regOpen, tourId, locked, onCopy, onErr }) {
  const { leaveTeam, removeMember, deleteTeam, registerTeam, unregisterTeam } = useMlbb();
  const [busy, setBusy] = useState(false);
  const isCaptain = team.captain === userKey;
  const rosterLocked = locked || (isReged && !regOpen);
  const rosterLockMessage = isReged && !regOpen ? M.team_reg_closed : M.team_locked;
  // основа готова (минимум 5) — можно подать заявку; потолок набора — 7
  const canRegister = team.members.length >= TEAM_MAIN;
  const subCount = Math.max(0, team.members.length - TEAM_MAIN);

  const run = async (action, lockedMessage) => {
    if (busy) return;
    setBusy(true);
    const err = await action();
    setBusy(false);
    if (err) onErr(err, lockedMessage);
  };

  // первые 5 участников — основной состав, следующие 2 — замена (по желанию)
  const mainSlots = Array.from({ length: TEAM_MAIN }, (_, i) => team.members[i] || null);
  const subSlots = Array.from({ length: TEAM_MAX - TEAM_MAIN }, (_, i) => team.members[TEAM_MAIN + i] || null);

  // одна строка состава: игрок или пустой слот (основной / замена)
  const slotRow = (m, i, isSub) => (
    <div key={m ? m.key : `empty-${i}`} className="flex items-center gap-3 border-b border-dashed border-line" style={{ padding: '9px 16px', minHeight: 46 }}>
      <span className="font-mono text-fg-faint" style={{ fontSize: 10, width: 14 }}>{i + 1}</span>
      {m ? (
        <>
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2">
              <span className="font-semibold text-fg truncate" style={{ fontSize: 13.5 }}>{m.nick || '—'}</span>
              {team.captain === m.key && <Pill color="var(--fed-gold)" bg="rgba(230,183,60,.1)" border="rgba(230,183,60,.35)">{M.team_cap}</Pill>}
              {m.key === userKey && <Pill color={VIO.bright} bg={VIO.soft} border={VIO.line}>{M.team_you}</Pill>}
            </span>
            <span className="block font-mono text-fg-faint truncate" style={{ fontSize: 10.5, marginTop: 2 }}>
              ID {m.gameId || '—'} · {m.tg || '—'}
            </span>
          </span>
          {isCaptain && !rosterLocked && m.key !== userKey && (
            <button onClick={() => run(() => removeMember(team.id, m.key), rosterLockMessage)} disabled={busy} className="bg-transparent border-none cursor-pointer text-fg-faint hover:text-accent-bright" title={M.team_kick}>
              <Ic name="x" size={15} />
            </button>
          )}
        </>
      ) : (
        <span className="text-fg-faint font-mono uppercase" style={{ fontSize: 10.5, letterSpacing: '.08em' }}>{isSub ? M.team_sub_slot : M.team_slot}</span>
      )}
    </div>
  );

  // подпись группы слотов: «Основной состав 5/5» / «Замена · по желанию 1/2»
  const groupLabel = (title, filled, total, hint) => (
    <div className="flex items-center gap-2 border-b border-line" style={{ padding: '7px 16px', background: 'rgba(8,8,10,.35)' }}>
      <span className="font-mono uppercase text-fg-muted" style={{ fontSize: 9, letterSpacing: '.14em' }}>{title}</span>
      {hint && <span className="font-mono text-fg-faint" style={{ fontSize: 9 }}>· {hint}</span>}
      <span className="flex-1" />
      <span className="font-mono text-fg-faint" style={{ fontSize: 9.5 }}>{filled}/{total}</span>
    </div>
  );

  return (
    <div className="rounded-2xl border border-line overflow-hidden" style={card}>
      <div className="flex items-center gap-3 flex-wrap border-b border-line" style={{ padding: '14px 16px' }}>
        <span
          className="flex items-center justify-center font-display font-bold text-white rounded-[9px]"
          style={{ minWidth: 42, height: 34, padding: '0 8px', fontSize: 13, background: `linear-gradient(140deg,${VIO.accent},${VIO.deep})`, boxShadow: `0 6px 16px -6px ${VIO.glow}` }}
        >
          {team.tag}
        </span>
        <span className="font-display font-bold text-fg" style={{ fontSize: 18 }}>{team.name}</span>
        <span className="font-mono text-fg-faint" style={{ fontSize: 11 }}>
          {Math.min(team.members.length, TEAM_MAIN)}/{TEAM_MAIN}
          {subCount > 0 && <span style={{ color: VIO.bright }}> +{subCount}</span>}
        </span>
        {isReged && <Pill color={VIO.bright} bg={VIO.soft} border={VIO.line} dot>{M.reg_accepted}</Pill>}
      </div>

      <div className="flex flex-col">
        {groupLabel(M.team_main, Math.min(team.members.length, TEAM_MAIN), TEAM_MAIN)}
        {mainSlots.map((m, i) => slotRow(m, i, false))}
        {groupLabel(M.team_subs, subCount, TEAM_MAX - TEAM_MAIN, M.team_optional)}
        {subSlots.map((m, i) => slotRow(m, TEAM_MAIN + i, true))}
      </div>

      <div className="flex items-center gap-2.5 flex-wrap" style={{ padding: '13px 16px' }}>
        <button
          onClick={() => onCopy(team.id)}
          disabled={rosterLocked}
          className="inline-flex items-center gap-2 h-9 px-3.5 rounded-[9px] cursor-pointer font-mono border border-line bg-surface-2 text-fg-soft hover:text-fg"
          style={{ fontSize: 12, letterSpacing: '.08em' }}
          title={M.team_copy}
        >
          {M.team_invite}: <b style={{ color: VIO.bright }}>{team.id}</b>
          <Ic name="doc" size={13} />
        </button>
        <span className="flex-1" />
        {rosterLocked ? (
          <span className="inline-flex items-start gap-2 text-fg-muted" style={{ fontSize: 12, lineHeight: 1.45, maxWidth: 420 }}>
            <Ic name="lock" size={14} color={VIO.bright} /> {rosterLockMessage}
          </span>
        ) : isCaptain ? (
          <button
            onClick={async () => {
              if (!window.confirm(M.team_del_confirm.replace('{name}', team.name))) return;
              await run(() => deleteTeam(team.id), rosterLockMessage);
            }}
            disabled={busy}
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-[9px] cursor-pointer border bg-transparent text-fg-muted hover:text-white transition-colors"
            style={{ fontSize: 12.5, borderColor: 'var(--fed-line-accent)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(225,18,23,.12)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <Ic name="trash" size={14} /> {M.team_disband}
          </button>
        ) : (
          <button
            onClick={() => {
              if (window.confirm(M.team_leave_confirm.replace('{name}', team.name))) {
                run(() => leaveTeam(userKey, team.id), rosterLockMessage);
              }
            }}
            disabled={busy}
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-[9px] cursor-pointer border border-line bg-transparent text-fg-muted hover:text-fg"
            style={{ fontSize: 12.5 }}
          >
            <Ic name="logout" size={14} /> {M.team_leave}
          </button>
        )}
      </div>

      {/* заявка на турнир */}
      <div className="border-t border-line" style={{ padding: '13px 16px' }}>
        {isReged ? (
          rosterLocked ? (
            <span className="inline-flex items-start gap-2 text-fg-muted" style={{ fontSize: 12.5, lineHeight: 1.45 }}>
              <Ic name="lock" size={14} color={VIO.bright} /> {rosterLockMessage}
            </span>
          ) : isCaptain && (
            <button
              onClick={() => run(() => unregisterTeam(team.id, tourId), M.reg_locked)}
              disabled={busy}
              className="h-10 px-4 rounded-[10px] cursor-pointer border border-line bg-transparent text-fg-muted hover:text-fg"
              style={{ fontSize: 13 }}
            >
              {M.reg_cancel}
            </button>
          )
        ) : !regOpen ? (
          <span className="inline-flex items-center gap-2 text-fg-muted" style={{ fontSize: 12.5 }}>
            <Ic name="info" size={14} color="var(--fed-accent-bright)" /> {M.reg_closed_pill}
          </span>
        ) : !isCaptain ? (
          <span className="text-fg-faint" style={{ fontSize: 12.5 }}>{M.reg_only_cap}</span>
        ) : !canRegister ? (
          <span className="inline-flex items-center gap-2 text-fg-muted" style={{ fontSize: 12.5 }}>
            <Ic name="info" size={14} color="var(--fed-gold)" /> {M.reg_need5}
          </span>
        ) : (
          <button
            onClick={() => run(() => registerTeam(team.id, tourId), M.reg_locked)}
            disabled={busy}
            className="w-full h-11 rounded-[11px] cursor-pointer border-none font-display font-semibold uppercase text-white"
            style={{ fontSize: 14, letterSpacing: '.03em', background: `linear-gradient(120deg,${VIO.accent},${VIO.deep})`, boxShadow: `0 10px 24px -10px ${VIO.glow}`, opacity: busy ? 0.65 : 1 }}
          >
            {busy ? '…' : `${M.reg_submit} →`}
          </button>
        )}
      </div>
    </div>
  );
}

export default function MlbbTournament({ cur, game = 'mlbb' }) {
  const { go, lang, tourTab, setTourTab, teamJoinCode, setTeamJoinCode } = useFedStore();
  const user = useAppStore((s) => s.user);
  const fire = useAppStore((s) => s.fire);
  const isCs2 = game === 'cs2';
  const isDota = game === 'dota2';
  const isPcTeam = isCs2 || isDota;
  const M = { ...MDICT[lang], ...(isDota ? DOTA_COPY[lang] : isCs2 ? CS2_COPY[lang] : {}) };
  if (isPcTeam) {
    M.st_done = lang === 'kz' ? 'Турнир аяқталды' : 'Турнир завершён';
    if (cur.status === 'done') {
      M.reg_closed_pill = M.st_done;
      M.teams_sub = lang === 'kz' ? 'Аяқталған турнирдің қатысушылары.' : 'Участники завершённого турнира.';
      M.reg_closed_d = lang === 'kz' ? 'Қатысушылар мен нәтижелер турнир бетінде сақталған.' : 'Участники и результаты сохранены на странице турнира.';
    } else if (registrationPending(cur)) {
      M.reg_closed_pill = registrationOpensLabel(cur, lang);
      M.reg_closed_d = lang === 'kz' ? 'Белгіленген уақытта тіркелу автоматты түрде ашылады.' : 'В указанное время регистрация откроется автоматически.';
    }
  }
  // Регистрация на турнир (regOpen из fed/data.js). false — приём заявок закрыт.
  const regOpen = teamRegistrationOpen(cur);
  const isQ2 = cur.id === 'mlbb-mca-quals2-2026';
  const T = isPcTeam ? CS2 : isQ2 ? Q2 : VIO;
  const prizeLabel = isPcTeam
    ? (cur.id === 'tdq0g2qy' ? '1 000 000 ₸' : (cur[`prize_${lang}`] || cur.prize_ru || '—'))
    : (cur.prizeUsd || '5 000 $');

  const userKey = userKeyOf(user);
  const mlbb = useMlbb();
  const profile = userKey && mlbb.game === game ? mlbb.profiles[userKey] : null;
  const myTeams = myTeamsOf(mlbb, userKey);
  // заявки этого турнира: у каждых квалификаций свой tour_id на бэке
  const regIds = regTeamIdsOf(mlbb, cur.id);
  const myRegedTeam = myTeams.find((t) => regIds.includes(t.id));
  const feeBadge = (teamId) => {
    const registration = mlbb.regs.find((r) => r.tourId === cur.id && r.teamId === teamId);
    if (!isPcTeam) return null;
    const confirmed = registration?.feeConfirmed === true;
    return <span style={{ display: 'block', marginTop: 6, fontSize: 12, lineHeight: 1.5, color: confirmed ? 'var(--fed-ok)' : 'var(--fed-gold)' }}>
      {lang === 'kz'
        ? (confirmed ? 'Жарна расталды' : 'Жарна расталмады')
        : (confirmed ? 'Взнос подтвержден' : 'Взнос не подтвержден')}
    </span>;
  };
  // сетка этого турнира уже играется — посев зафиксирован. Отзыв заявки или
  // роспуск команды сдвинули бы весь посев и сбили сыгранные пары, поэтому
  // кнопки прячем (жёсткий запрет — на бэке, _tour_started в fed_teams.py).
  const bracketLive = Object.values(mlbb.results || {}).some((r) => r && (r.opened || r.status));

  const [pf, setPf] = useState({ gameId: profile?.gameId || '', nick: profile?.nick || user?.nickname || '', tg: profile?.tg || '' });
  const [nt, setNt] = useState({ name: '', tag: '' });
  const [joinCode, setJoinCode] = useState(teamJoinCode || '');
  const [expandedTeam, setExpandedTeam] = useState('');
  const [actionBusy, setActionBusy] = useState('');
  const pfDirty = useRef(false);

  // синхронизация с бэкендом: подтянуть состояние + поллинг, пока страница
  // открыта. cur.id в зависимостях — переход между квалификациями (#1 ↔ #2)
  // не размонтирует компонент, стор нужно перевести на новый турнир.
  useEffect(() => mlbb.startPolling(cur.id, game), [cur.id, game]); // eslint-disable-line react-hooks/exhaustive-deps

  // профиль пришёл с сервера (новое устройство) — подставляем в форму,
  // если пользователь ещё не начал её редактировать
  useEffect(() => {
    if (profile && !pfDirty.current) {
      setPf({ gameId: profile.gameId || '', nick: profile.nick || user?.nickname || '', tg: profile.tg || '' });
    }
  }, [profile?.gameId, profile?.nick, profile?.tg]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (teamJoinCode) setJoinCode(teamJoinCode);
  }, [teamJoinCode]);

  const editPf = (patch) => { pfDirty.current = true; setPf((p) => ({ ...p, ...patch })); };

  // регламент турнира: свой у каждых квалификаций (rules_ru/rules_kz в data.js),
  // фолбэк — общий регламент MLBB
  const rules = cur[`rules_${lang}`] || cur.rules_ru || (lang === 'ru' ? MLBB_RULES_RU : MLBB_RULES_KZ);

  const TABS = [
    ['overview', M.tab_overview],
    ...(MATCH_CENTER_PUBLIC_ENABLED && !isDota ? [['matches', M.tab_matches]] : []),
    ...(regOpen ? [['reg', M.tab_reg]] : []),
    ['teams', M.tab_teams],
    ['bracket', M.tab_bracket],
  ];
  // При смене вкладки сохраняем позицию страницы: навигация находится ниже
  // hero, и возврат к началу заставлял пользователя каждый раз скроллить назад.
  const goTab = (k) => {
    setTourTab(k);
    if (!isPcTeam || k !== 'reg' || typeof window === 'undefined') return;
    window.setTimeout(() => {
      const target = document.getElementById('cs2-tournament-panel');
      if (!target) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }, 80);
  };
  const activeTab = TABS.some(([k]) => k === tourTab) ? tourTab : 'overview';

  const copyCode = async (c) => {
    try {
      const invite = `${window.location.origin}/#tournament/${encodeURIComponent(cur.id)}?join=${encodeURIComponent(c)}`;
      await navigator.clipboard.writeText(invite);
      fire(M.team_copied, 'success');
    } catch (e) {
      fire(M.err_generic, 'error');
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    if (actionBusy) return;
    if (!pf.gameId.trim() || !pf.nick.trim() || !pf.tg.trim()) { fire(M.fill_all, 'error'); return; }
    const tg = pf.tg.trim().startsWith('@') ? pf.tg.trim() : '@' + pf.tg.trim();
    setActionBusy('profile');
    const err = await mlbb.saveProfile(userKey, { gameId: pf.gameId.trim(), nick: pf.nick.trim(), tg });
    setActionBusy('');
    if (err) { fire(errMsg(err), 'error'); return; }
    pfDirty.current = false;
    fire(M.prof_saved, 'success');
  };

  const errMsg = (err) =>
    ({
      'not-found': M.err_notfound,
      full: M.err_full,
      already: M.err_already,
      'no-profile': M.err_noprofile,
      'have-team': M.team_have_one,
      'reg-closed': M.reg_closed_pill,
      'reg-not-open': registrationOpensLabel(cur, lang) || M.reg_closed_pill,
      'wrong-game': M.err_generic,
      offline: M.err_offline,
    }[err] || M.err_generic);

  // Ошибка действия с командой. 'tour-started' прилетает, когда команда играет
  // в ДРУГОМ турнире (напр. открыта страница вторых квал, а сетка первых уже
  // сыграна) — локально это не видно, поэтому объясняем текстом действия.
  const showErr = (err, lockedMsg) => fire(err === 'tour-started' ? lockedMsg : errMsg(err), 'error');

  const createTeam = async (e) => {
    e.preventDefault();
    if (actionBusy) return;
    if (!profile) { fire(M.err_noprofile, 'error'); return; }
    if (myTeams.length > 0) { fire(M.team_have_one, 'error'); return; }
    if (!nt.name.trim()) { fire(M.fill_all, 'error'); return; }
    setActionBusy('create');
    const err = await mlbb.createTeam(userKey, nt);
    setActionBusy('');
    if (err) { fire(errMsg(err), 'error'); return; }
    setNt({ name: '', tag: '' });
  };

  const joinTeam = async (e) => {
    e.preventDefault();
    if (actionBusy) return;
    if (myTeams.length > 0) { fire(M.team_have_one, 'error'); return; }
    if (!profile) {
      goTab('reg');
      fire(M.err_noprofile, 'error');
      return;
    }
    setActionBusy('join');
    const err = await mlbb.joinTeam(userKey, joinCode);
    setActionBusy('');
    if (err) { fire(errMsg(err), 'error'); return; }
    setJoinCode('');
    setTeamJoinCode('');
  };

  const goAuth = (mode) => {
    // после входа вернуться на вкладку регистрации этого турнира
    useFedStore.getState().setAuthReturn({ route: 'tournament', extra: { tourId: cur.id, tourTab: 'reg' } });
    go('auth', { authMode: mode });
  };

  const steps = [
    { t: M.step1, d: M.step1_d, done: !!user },
    { t: M.step2, d: M.step2_d, done: !!(profile && profile.gameId) },
    { t: M.step3, d: M.step3_d, done: myTeams.length > 0 },
    { t: M.step4, d: M.step4_d, done: !!myRegedTeam },
  ];

  const TAB_ICONS = { overview: 'info', matches: 'video', reg: 'edit', teams: 'community', bracket: 'layers' };

  const pick = (base) => cur[`${base}_${lang}`] || cur[`${base}_ru`] || '';
  // Плитку показываем, только если факт заполнен: у турнира без объявленных
  // дат (идёт лишь приём заявок) «Регистрация до» и «Старт матчей» отпадают.
  const facts = [
    ['inbox', M.ki_regclose, pick('regClose')],
    ['calendar', M.ki_date, isCs2 && cur.id === 'tdq0g2qy' ? (lang === 'kz' ? '2026 жылғы 3–4 қазан' : '3–4 октября 2026') : pick('date')],
    ['globe', M.ki_place, pick('format')],
    ['trophy', M.ki_format, isPcTeam ? String(regIds.length) : (cur.slots ? String(cur.slots) : '')],
    ...(isDota ? [['calendar', lang === 'kz' ? 'Тіркелудің ашылуы' : 'Открытие регистрации', registrationOpensLabel(cur, lang)], ['info', lang === 'kz' ? 'Қатысу жарнасы' : 'Взнос за участие', pick('fee')]] : []),
  ].filter(([, , v]) => v);

  return (
    <div id={isPcTeam ? 'cs2-tournament-panel' : undefined} className={isPcTeam ? `cs2-team-page${isDota ? ' dota-team-page' : ''}` : isQ2 ? 'mlbb-q2-page' : ''}>
      {/* ===== табы: иконка + подпись, 4 в ряд — помещаются на телефоне ===== */}
      <div
        className="grid gap-1.5 p-[5px] bg-surface border border-line rounded-[14px] mb-6 mlbb-tabs"
        style={{ '--mlbb-tab-count': TABS.length, gridTemplateColumns: `repeat(${TABS.length},minmax(0,1fr))` }}
      >
        {TABS.map(([k, label]) => {
          const on = activeTab === k;
          return (
            <button
              key={k}
              onClick={() => goTab(k)}
              className="flex flex-col items-center justify-center gap-1 rounded-[10px] cursor-pointer border-none transition-colors"
              style={{
                padding: '9px 4px', minHeight: 56,
                background: on ? `linear-gradient(120deg,${T.accent},${T.deep})` : 'transparent',
                color: on ? '#fff' : 'var(--fed-fg-muted)',
                boxShadow: on ? `0 8px 20px -10px ${T.glow}` : 'none',
              }}
            >
              <Ic name={TAB_ICONS[k]} size={18} sw={1.9} />
              <span className="font-display font-semibold uppercase leading-none text-center" style={{ fontSize: 11, letterSpacing: '.02em' }}>{label}</span>
            </button>
          );
        })}
      </div>

      {/* ===== ОБЗОР ===== */}
      {activeTab === 'overview' && (
        <div className="flex flex-col gap-5">
          {/* ОБЛОЖКА-ПОСТЕР турнира */}
          {cur.image && !isQ2 && !isPcTeam && (
            <Rv className="relative overflow-hidden rounded-[20px] border" style={{ borderColor: T.line, boxShadow: `0 0 46px -22px ${T.glow}` }}>
              <img src={cur.image} alt={cur.title} className="w-full block" style={{ maxHeight: 460, objectFit: 'cover', objectPosition: 'center 22%' }} />
            </Rv>
          )}

          {/* КАРТОЧКА ЧЕМПИОНА: показывается, когда квалификации завершены */}
          {cur.status === 'done' && cur.champ && (
            <Rv
              className="relative overflow-hidden rounded-[20px]"
              style={{
                padding: 26,
                background: 'linear-gradient(160deg,rgba(230,183,60,.14),var(--fed-surface) 55%)',
                border: '1px solid rgba(230,183,60,.4)',
                boxShadow: 'var(--fed-shadow),var(--fed-glow-gold)',
              }}
            >
              <div className="font-mono uppercase text-gold" style={{ fontSize: 10, letterSpacing: '.2em' }}>{M.champ_t}</div>
              <div className="flex items-center gap-4 flex-wrap" style={{ marginTop: 14 }}>
                <Ic name="trophy" size={44} color="var(--fed-gold)" />
                <div>
                  <div className="font-display font-bold text-fg" style={{ fontSize: 28 }}>{cur.champ}</div>
                  <div className="text-fg-muted" style={{ fontSize: 14, marginTop: 4 }}>{cur.champPlayer}</div>
                </div>
              </div>
              {cur.runnerUp && (
                <div className="flex items-center gap-2.5 text-fg-soft" style={{ marginTop: 16, paddingTop: 16, borderTop: '1px dashed var(--fed-line)', fontSize: 13.5 }}>
                  <Ic name="medal" size={16} color="var(--fed-fg-muted)" />
                  {M.champ_runnerup}: <b className="text-fg">{cur.runnerUp}</b>
                </div>
              )}
              <div className="font-mono uppercase text-fg-faint" style={{ fontSize: 10.5, letterSpacing: '.1em', marginTop: 10 }}>{M.champ_slots}</div>
            </Rv>
          )}

          {/* ГЛАВНАЯ КАРТОЧКА: приз + факты + кнопка регистрации сразу вверху */}
          <Rv
            className={`relative overflow-hidden rounded-[20px]${isQ2 ? ' mlbb-q2-prize-card' : ''}`}
            style={{
              background: isQ2 ? undefined : `radial-gradient(ellipse at 85% -30%,${T.soft},transparent 58%),linear-gradient(160deg,rgba(109,40,217,.18),var(--fed-surface) 62%)`,
              backgroundImage: isQ2 ? `linear-gradient(90deg,rgba(10,8,12,.96) 0%,rgba(10,8,12,.82) 52%,rgba(10,8,12,.12) 100%),url('${encodeURI('/assets/млбб 2.png?v=1')}')` : undefined,
              backgroundSize: isQ2 ? 'cover' : undefined,
              backgroundPosition: isQ2 ? 'center' : undefined,
              border: `1px solid ${T.line}`,
              boxShadow: `var(--fed-shadow),0 0 46px -20px ${T.glow}`,
            }}
          >
            <div className="relative flex flex-col" style={{ padding: 22 }}>
              <div className="flex items-center gap-2.5 flex-wrap" style={{ marginBottom: 18 }}>
                {regOpen ? (
                  <Pill color={T.bright} bg={T.soft} border={T.line} dot>{M.st_reg_open}</Pill>
                ) : cur.status === 'done' ? (
                  <Pill color="var(--fed-gold)" bg="rgba(230,183,60,.1)" border="rgba(230,183,60,.35)">{M.st_done}</Pill>
                ) : (
                  <Pill color="var(--fed-accent-bright)" bg="rgba(225,18,23,.1)" border="var(--fed-line-accent)">{M.reg_closed_pill}</Pill>
                )}
                {cur.qualifierNo && (
                  <Pill color="var(--fed-gold)" bg="rgba(230,183,60,.1)" border="rgba(230,183,60,.35)">{M.qual_pill}{cur.qualifierNo}</Pill>
                )}
                {(cur.org || M.org_default || !isPcTeam) && (
                  <span className="font-mono uppercase text-fg-faint" style={{ fontSize: 10, letterSpacing: '.12em' }}>
                    {M.ov_org}: {cur.org || M.org_default || 'QNEA'}
                  </span>
                )}
              </div>

              {/* приз MCA League */}
              <div className="flex items-center gap-4">
                <span
                  className="flex-shrink-0 overflow-hidden rounded-2xl"
                  style={{ width: 60, height: 60, border: `1px solid ${T.line}`, boxShadow: `0 0 26px -8px ${T.glow}` }}
                >
                  {cur.image ? <img src={cur.image} alt="" className="w-full h-full" style={{ objectFit: 'cover', objectPosition: 'center 20%' }} /> : <span className="flex w-full h-full items-center justify-center"><Ic name="trophy" size={30} color={T.bright} /></span>}
                </span>
                <div className="min-w-0">
                  <div className="font-mono uppercase" style={{ fontSize: 9.5, letterSpacing: '.18em', color: T.bright }}>{M.ov_prize}</div>
                  <div className="flex items-end gap-2 flex-wrap" style={{ marginTop: 4 }}>
                    <span className="font-display font-bold text-fg" style={{ fontSize: isDota ? 'clamp(24px,5vw,36px)' : 'clamp(34px,9vw,50px)', lineHeight: 1.15 }}>{prizeLabel}</span>
                    {(!isPcTeam || Number(cur.slots) > 0) && (
                      <span className="font-display font-semibold" style={{ fontSize: 15, paddingBottom: 4, color: T.bright }}>· {cur.slots || 2} {M.ov_slots}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* факты — компактная сетка, единственный источник (без дублей) */}
              <div className="grid gap-2 mt-5" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))' }}>
                {facts.map(([ic, l, v]) => (
                  <div key={l} className="flex items-center gap-2.5 rounded-xl border border-line" style={{ padding: '9px 11px', background: 'rgba(8,8,10,.4)' }}>
                    <span className="flex-shrink-0 flex items-center justify-center rounded-lg" style={{ width: 30, height: 30, background: T.soft, border: `1px solid ${T.line}` }}>
                      <Ic name={ic} size={14} color={T.bright} />
                    </span>
                    <span className="min-w-0">
                      <span className="block font-mono uppercase text-fg-faint" style={{ fontSize: 8.5, letterSpacing: '.12em' }}>{l}</span>
                      <span className="block font-semibold text-fg" style={{ fontSize: 13, marginTop: 1, overflowWrap: 'anywhere' }}>{v}</span>
                    </span>
                  </div>
                ))}
              </div>

              {/* CTA — крупная, сразу под фактами. На телефоне кнопки в столбик.
                  Регистрация закрыта — вместо кнопок показываем уведомление. */}
              {regOpen ? (
                <div className="flex gap-2.5 max-[560px]:flex-col" style={{ marginTop: 18 }}>
                  <button
                    onClick={() => goTab('reg')}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 text-center leading-[1.1] min-h-[52px] px-3 py-2 rounded-[13px] cursor-pointer border-none font-display font-bold uppercase text-white"
                    style={{ fontSize: 14, letterSpacing: '.02em', background: `linear-gradient(120deg,${T.accent},${T.deep})`, boxShadow: `0 14px 30px -10px ${T.glow}` }}
                  >
                    {M.ov_cta} →
                  </button>
                  <button
                    onClick={() => goTab('reg')}
                    className="flex-1 inline-flex items-center justify-center gap-2 text-center leading-[1.1] min-h-[52px] px-3 py-2 rounded-[13px] cursor-pointer font-display font-bold uppercase"
                    style={{ fontSize: 14, letterSpacing: '.02em', background: T.soft, border: `1px solid ${T.line}`, color: T.bright }}
                  >
                    <Ic name="search" size={16} sw={2.2} className="flex-shrink-0" /> {M.ov_find}
                  </button>
                </div>
              ) : (
                <div
                  className="flex items-center gap-3 rounded-[13px]"
                  style={{ marginTop: 18, padding: '14px 16px', background: 'rgba(225,18,23,.08)', border: '1px solid var(--fed-line-accent)' }}
                >
                  <Ic name="info" size={20} color="var(--fed-accent-bright)" className="flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="font-display font-bold uppercase text-fg" style={{ fontSize: 13.5, letterSpacing: '.02em' }}>{M.reg_closed_pill}</div>
                    <div className="text-fg-muted" style={{ fontSize: 12, lineHeight: 1.45, marginTop: 2 }}>{M.reg_closed_d}</div>
                  </div>
                </div>
              )}
              {cur.tgPost && (
                <a
                  href={cur.tgPost} target="_blank" rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 mt-3 text-fg-muted hover:text-fg"
                  style={{ fontSize: 13, textDecoration: 'none' }}
                >
                  <Ic name="telegram" size={15} color={T.bright} /> {M.ov_post} →
                </a>
              )}
            </div>
          </Rv>

          {/* КАК УЧАСТВОВАТЬ: 4 шага, всегда видно что делать */}
          {cur.status !== 'done' && <Rv className="rounded-2xl bg-surface border border-line" style={{ padding: 20 }}>
            <div className="font-mono uppercase text-fg-muted" style={{ fontSize: 10, letterSpacing: '.2em', marginBottom: 16 }}>{M.steps_t}</div>
            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))' }}>
              {steps.map((s, i) => (
                <div key={s.t} className="flex gap-3 items-start">
                  <StepBadge n={i + 1} done={s.done} />
                  <div>
                    <div className="font-display font-bold text-fg" style={{ fontSize: 14.5 }}>{s.t}</div>
                    <div className="text-fg-muted" style={{ fontSize: 12, lineHeight: 1.45, marginTop: 3 }}>{s.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </Rv>}

          {/* О ТУРНИРЕ + РЕГЛАМЕНТ */}
          <div className="fed-split-2">
            <Rv className="rounded-2xl bg-surface border border-line" style={{ padding: 22 }}>
              <h3 className="mb-3" style={{ fontSize: 19 }}>{M.ov_about}</h3>
              <p className="text-fg-soft" style={{ fontSize: 14.5, lineHeight: 1.65 }}>{cur['about_' + lang] || cur.about_ru}</p>
              <div className="flex gap-2 flex-wrap" style={{ marginTop: 14 }}>
                {[...M.chips, isPcTeam ? M.qual_pill : `${M.qual_pill}${cur.qualifierNo || 1}`].map((c) => (
                  <Pill key={c} color={T.bright} bg={T.soft} border={T.line}>{c}</Pill>
                ))}
              </div>
            </Rv>
            <Rv delay={0.06} className="rounded-2xl bg-surface border border-line" style={{ padding: 22 }}>
              <h3 className="mb-2" style={{ fontSize: 19 }}>{M.ov_rules}</h3>
              <div className="flex flex-col">
                {rules.map((r, i) => (
                  <div key={i} className="flex gap-3 items-start border-b border-dashed border-line text-fg-soft last:border-b-0" style={{ padding: '10px 0', fontSize: 13.5, lineHeight: 1.5 }}>
                    <span className="flex-shrink-0" style={{ color: T.bright }}>▸</span>
                    <span>{r}</span>
                  </div>
                ))}
              </div>
            </Rv>
          </div>
        </div>
      )}

      {MATCH_CENTER_PUBLIC_ENABLED && activeTab === 'matches'
        && <MatchCenter tournamentKey={cur.id} discipline={game} />}

      {/* ===== РЕГИСТРАЦИЯ ===== */}
      {activeTab === 'reg' && (
        <div className={`fed-split-2${isPcTeam ? ' cs2-reg-inline' : ''}`} style={{ alignItems: 'start' }}>
          {/* левая колонка: шаги + статус */}
          <Rv className="flex flex-col gap-5">
            <div className="rounded-2xl bg-surface border border-line" style={{ padding: 24 }}>
              <div className="font-mono uppercase text-fg-muted" style={{ fontSize: 10, letterSpacing: '.2em', marginBottom: 16 }}>{M.steps_t}</div>
              <div className="flex flex-col gap-4">
                {steps.map((s, i) => (
                  <div key={s.t} className="flex gap-3.5 items-start">
                    <StepBadge n={i + 1} done={s.done} />
                    <div>
                      <div className="font-display font-bold text-fg" style={{ fontSize: 15.5 }}>{s.t}</div>
                      <div className="text-fg-muted" style={{ fontSize: 12.5, lineHeight: 1.5, marginTop: 3 }}>{s.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {myRegedTeam && (
              <div className="rounded-2xl" style={{ padding: 24, background: 'linear-gradient(160deg,rgba(67,217,106,.1),var(--fed-surface) 60%)', border: '1px solid rgba(67,217,106,.35)' }}>
                <div className="flex items-center gap-2.5">
                  <Ic name="check" size={20} color="var(--fed-ok)" sw={2.6} />
                  <span className="font-display font-bold text-fg" style={{ fontSize: 17 }}>{M.reg_done}</span>
                </div>
                <p className="text-fg-muted" style={{ fontSize: 13, lineHeight: 1.6, marginTop: 8 }}>{M.reg_done_d}</p>
                {feeBadge(myRegedTeam.id)}
                <button
                  onClick={() => goTab(isPcTeam ? 'teams' : 'bracket')}
                  className="h-10 px-4 rounded-[10px] cursor-pointer border-none font-display font-semibold uppercase text-white"
                  style={{ marginTop: 12, fontSize: 13, background: `linear-gradient(120deg,${VIO.accent},${VIO.deep})` }}
                >
                  {M.reg_open_bracket} →
                </button>
              </div>
            )}
          </Rv>

          {/* правая колонка: аккаунт → профиль → команда */}
          <Rv delay={0.08} className="flex flex-col gap-5">
            {!user ? (
              <div className="rounded-2xl border border-line text-center" style={{ ...card, padding: '36px 26px' }}>
                <div className="mx-auto flex items-center justify-center rounded-full" style={{ width: 58, height: 58, background: VIO.soft, border: `1px solid ${VIO.line}` }}>
                  <Ic name="user" size={26} color={VIO.bright} />
                </div>
                <h3 style={{ fontSize: 21, marginTop: 16 }}>{M.acc_need}</h3>
                <p className="text-fg-muted" style={{ fontSize: 13.5, marginTop: 8 }}>{M.acc_need_d}</p>
                <div className="flex gap-2.5 justify-center flex-wrap" style={{ marginTop: 18 }}>
                  <button
                    onClick={() => goAuth('signup')}
                    className="h-11 px-6 rounded-[11px] cursor-pointer border-none font-display font-semibold uppercase text-white"
                    style={{ fontSize: 14, background: `linear-gradient(120deg,${VIO.accent},${VIO.deep})`, boxShadow: `0 10px 24px -10px ${VIO.glow}` }}
                  >
                    {M.acc_signup}
                  </button>
                  <button onClick={() => goAuth('login')} className="fed-btn-ghost h-11 px-6 rounded-[11px]" style={{ fontSize: 14 }}>
                    {M.acc_login}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="inline-flex items-center gap-2.5 self-start rounded-full border border-line bg-surface" style={{ padding: '7px 14px 7px 9px' }}>
                  <span className="flex items-center justify-center rounded-full" style={{ width: 22, height: 22, background: 'rgba(67,217,106,.14)', border: '1px solid rgba(67,217,106,.4)', color: 'var(--fed-ok)' }}>
                    <Ic name="check" size={12} sw={3} />
                  </span>
                  <span className="text-fg-muted" style={{ fontSize: 13 }}>{M.acc_done} <b className="text-fg">{user.nickname || user.email}</b></span>
                </div>

                {/* профиль игрока */}
                <form onSubmit={saveProfile} className="rounded-2xl border border-line" style={{ ...card, padding: 22 }}>
                  <h3 style={{ fontSize: 18, marginBottom: 14 }}>{M.prof_t}</h3>
                  <div className="grid grid-cols-2 gap-3 max-[560px]:grid-cols-1">
                    <Field label={M.prof_gameid}>
                      <input
                        className="fed-input"
                        style={{ height: 44 }}
                        inputMode={isCs2 ? 'url' : 'numeric'}
                        placeholder={isCs2 ? 'Steam ID или steamcommunity.com/id/…' : '123456789'}
                        value={pf.gameId}
                        onChange={(e) => editPf({ gameId: isCs2 ? e.target.value : e.target.value.replace(/[^\d() ]/g, '') })}
                      />
                    </Field>
                    <Field label={M.prof_nick}>
                      <input className="fed-input" style={{ height: 44 }} placeholder="Miya_kz" value={pf.nick} onChange={(e) => editPf({ nick: e.target.value })} />
                    </Field>
                  </div>
                  <div className="grid gap-3 mt-3" style={{ gridTemplateColumns: '1fr auto' }}>
                    <Field label={M.prof_tg}>
                      <input className="fed-input" style={{ height: 44 }} placeholder="@username" value={pf.tg} onChange={(e) => editPf({ tg: e.target.value })} />
                    </Field>
                    <button type="submit" disabled={!!actionBusy} className="self-end h-11 px-5 rounded-[10px] cursor-pointer border-none font-display font-semibold uppercase text-white" style={{ fontSize: 13, background: `linear-gradient(120deg,${VIO.accent},${VIO.deep})`, opacity: actionBusy ? 0.65 : 1 }}>
                      {actionBusy === 'profile' ? '…' : M.prof_save}
                    </button>
                  </div>
                  <div className="flex items-center gap-2 text-fg-faint" style={{ fontSize: 11.5, marginTop: 10 }}>
                    <Ic name="info" size={13} color={VIO.bright} /> {M.prof_hint}
                  </div>
                </form>

                {/* команды */}
                <div className="flex flex-col gap-4">
                  <h3 style={{ fontSize: 18 }}>{M.team_t}</h3>
                  {myTeams.length === 0 && <div className="text-fg-muted" style={{ fontSize: 13.5 }}>{M.team_none}</div>}
                  {myTeams.map((t) => (
                    <TeamCard key={t.id} team={t} M={M} userKey={userKey} isReged={regIds.includes(t.id)} regOpen={regOpen} tourId={cur.id} locked={bracketLive} onCopy={copyCode} onErr={showErr} />
                  ))}

                  {/* создать/вступить — только если игрок ещё не в команде:
                      на турнир заявляется одна команда, состоять можно в одной */}
                  {myTeams.length === 0 ? (
                    <div className="grid grid-cols-2 gap-4 max-[640px]:grid-cols-1">
                      <form onSubmit={createTeam} className="rounded-2xl border border-line flex flex-col gap-3" style={{ ...card, padding: 18 }}>
                        <div className="font-mono uppercase text-fg-muted" style={{ fontSize: 10, letterSpacing: '.16em' }}>{M.team_create_t}</div>
                        <Field label={M.team_name}>
                          <input className="fed-input" style={{ height: 42 }} value={nt.name} onChange={(e) => setNt({ ...nt, name: e.target.value })} />
                        </Field>
                        <Field label={M.team_tag}>
                          <input className="fed-input" style={{ height: 42 }} maxLength={5} value={nt.tag} onChange={(e) => setNt({ ...nt, tag: e.target.value.toUpperCase() })} />
                        </Field>
                        <button type="submit" disabled={!!actionBusy} className="fed-btn-ghost h-10 rounded-[10px]" style={{ fontSize: 13.5, opacity: actionBusy ? 0.65 : 1 }}>{actionBusy === 'create' ? '…' : M.team_create}</button>
                      </form>
                      <form onSubmit={joinTeam} className="rounded-2xl border border-line flex flex-col gap-3" style={{ ...card, padding: 18 }}>
                        <div className="font-mono uppercase text-fg-muted" style={{ fontSize: 10, letterSpacing: '.16em' }}>{M.team_join_t}</div>
                        <Field label={M.team_code}>
                          <input className="fed-input font-mono" style={{ height: 42, letterSpacing: '.2em' }} maxLength={5} placeholder="A1B2C" value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} />
                        </Field>
                        <button type="submit" disabled={!!actionBusy} className="fed-btn-ghost h-10 rounded-[10px] mt-auto" style={{ fontSize: 13.5, opacity: actionBusy ? 0.65 : 1 }}>{actionBusy === 'join' ? '…' : M.team_join}</button>
                      </form>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2.5 rounded-xl border border-line" style={{ padding: '12px 14px', background: 'rgba(8,8,10,.4)' }}>
                      <Ic name="info" size={15} color={VIO.bright} />
                      <span className="text-fg-muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>{M.team_have_one}</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </Rv>
        </div>
      )}

      {/* ===== КОМАНДЫ ===== */}
      {activeTab === 'teams' && (
        <div>
          <Rv style={{ marginBottom: 18 }}>
            <h3 style={{ fontSize: 20 }}>{M.teams_t}</h3>
            <p className="text-fg-muted" style={{ fontSize: 13.5, marginTop: 4 }}>{M.teams_sub}</p>
          </Rv>
          {isPcTeam && regOpen && (
            <Rv className="cs2-teams-quick-join" style={{ marginBottom: 18 }}>
              <div>
                <div className="font-display font-bold uppercase text-fg" style={{ fontSize: 16 }}>
                  {lang === 'kz' ? 'Команда коды бар ма?' : 'Уже есть код команды?'}
                </div>
                <p className="text-fg-muted" style={{ fontSize: 12.5, marginTop: 3 }}>
                  {lang === 'kz' ? 'Капитаннан шақыру кодын алып, құрамға бірден қосылыңыз.' : 'Получите код у капитана и сразу вступите в состав.'}
                </p>
              </div>
              {!user ? (
                <button type="button" onClick={() => goAuth('login')} className="fed-btn-ghost">
                  {lang === 'kz' ? 'Кіру' : 'Войти в аккаунт'}
                </button>
              ) : myTeams.length === 0 ? (
                <form onSubmit={joinTeam} className="cs2-teams-quick-join__form">
                  <input
                    className="fed-input font-mono"
                    maxLength={5}
                    aria-label={M.team_code}
                    placeholder="A1B2C"
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  />
                  <button type="submit" disabled={!!actionBusy}>{actionBusy === 'join' ? '…' : M.team_join}</button>
                </form>
              ) : (
                <Pill color={T.bright} bg={T.soft} border={T.line}>{lang === 'kz' ? 'Сіз командадасыз' : 'Вы уже в команде'}</Pill>
              )}
            </Rv>
          )}
          {(isPcTeam && cur.status !== 'done' ? mlbb.teams.length : regIds.length) === 0 && (
            <Rv className="rounded-2xl bg-surface border border-line text-center" style={{ padding: '44px 24px' }}>
              <div className="font-display font-bold uppercase text-fg" style={{ fontSize: 20 }}>{M.teams_empty}</div>
              {regOpen && (
                <button
                  onClick={() => goTab('reg')}
                  className="h-11 px-6 rounded-[11px] cursor-pointer border-none font-display font-semibold uppercase text-white"
                  style={{ marginTop: 16, fontSize: 14, background: `linear-gradient(120deg,${VIO.accent},${VIO.deep})` }}
                >
                  {M.ov_cta} →
                </button>
              )}
            </Rv>
          )}
          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(min(100%,280px),1fr))' }}>
            {(isPcTeam && cur.status !== 'done' ? mlbb.teams.map((t) => t.id) : regIds).map((id, i) => {
              const t = mlbb.teams.find((x) => x.id === id);
              if (!t) return null;
              const mine = userKey && t.members.some((m) => m.key === userKey);
              const registered = regIds.includes(t.id);
              const expanded = expandedTeam === t.id;
              return (
                <Rv key={id} delay={(i % 4) * 0.05} className={`rounded-2xl border overflow-hidden cs2-public-team${expanded ? ' is-open' : ''}`} style={{ ...card, borderColor: mine ? VIO.line : 'var(--fed-line)', boxShadow: mine ? `0 0 32px -14px ${VIO.glow}` : undefined }}>
                  <button type="button" onClick={() => setExpandedTeam(expanded ? '' : t.id)} className="cs2-public-team__head" aria-expanded={expanded}>
                    <span className="flex items-center justify-center font-display font-bold text-white rounded-[9px]" style={{ minWidth: 40, height: 32, padding: '0 8px', fontSize: 12.5, background: `linear-gradient(140deg,${VIO.accent},${VIO.deep})` }}>
                      {t.tag}
                    </span>
                    <span className="min-w-0 flex-1 text-left flex flex-col gap-1.5">
                      <span className="block font-display font-bold text-fg truncate" style={{ fontSize: 16, lineHeight: 1.35 }}>{t.name}</span>
                      <span className="block font-mono uppercase text-fg-faint" style={{ fontSize: 10, lineHeight: 1.5, letterSpacing: '.04em', overflowWrap: 'anywhere' }}>{t.members.length}/{TEAM_MAIN} · {registered ? (lang === 'kz' ? 'өтінім берілді' : 'заявка подана') : (lang === 'kz' ? 'құрам жиналуда' : 'состав собирается')}</span>
                      {feeBadge(t.id)}
                    </span>
                    {mine && <Pill color={VIO.bright} bg={VIO.soft} border={VIO.line}>{M.team_you}</Pill>}
                    <Ic name="chevD" size={18} className="cs2-public-team__chev" />
                  </button>
                  <div style={{ padding: '10px 15px 14px' }}>
                    <div className="font-mono uppercase text-fg-faint" style={{ fontSize: 9.5, letterSpacing: '.14em', marginBottom: 8 }}>
                      {M.teams_members} · {Math.min(t.members.length, TEAM_MAIN)}/{TEAM_MAIN}
                      {t.members.length > TEAM_MAIN && <span style={{ color: VIO.bright }}> +{t.members.length - TEAM_MAIN}</span>}
                    </div>
                    <div className="flex flex-col gap-1.5">
                      {t.members.map((m, mi) => (
                        <div key={m.key} className="flex items-center gap-2.5">
                          <span className="rounded-full flex-shrink-0" style={{ width: 5, height: 5, background: mi < TEAM_MAIN ? VIO.accent : 'var(--fed-fg-faint)' }} />
                          <span className="text-fg-soft truncate" style={{ fontSize: 12.5 }}>{m.nick}</span>
                          {t.captain === m.key && <Ic name="medal" size={13} color="var(--fed-gold)" />}
                          {mi >= TEAM_MAIN && <span className="font-mono text-fg-faint" style={{ fontSize: 9.5 }}>· {M.team_subs.toLowerCase()}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                  {expanded && (
                    <div className="cs2-public-team__detail">
                      <div className="cs2-public-team__stats">
                        <span><b>{t.members.length}</b>{lang === 'kz' ? 'ойыншы' : 'участников'}</span>
                        <span><b>{Math.max(0, TEAM_MAX - t.members.length)}</b>{lang === 'kz' ? 'бос орын' : 'свободных мест'}</span>
                        <span><b>{registered ? '✓' : '—'}</b>{lang === 'kz' ? 'турнир өтінімі' : 'заявка на турнир'}</span>
                      </div>
                      <div className="cs2-public-team__profiles">
                        {t.members.map((m) => (
                          <article key={m.key}>
                            <div>
                              <strong>{m.nick || (lang === 'kz' ? 'Ойыншы' : 'Игрок')}</strong>
                              {t.captain === m.key && <span>{lang === 'kz' ? 'капитан' : 'капитан'}</span>}
                            </div>
                            <p>{isDota ? 'Dota 2 ID' : 'Steam'}: {m.gameId || '—'}</p>
                            {m.tg && <a href={`https://t.me/${m.tg.replace(/^@/, '')}`} target="_blank" rel="noopener noreferrer">{m.tg}</a>}
                          </article>
                        ))}
                      </div>
                    </div>
                  )}
                </Rv>
              );
            })}
          </div>
        </div>
      )}

      {/* ===== СЕТКА ===== */}
      {activeTab === 'bracket' && (isCs2 && Array.isArray(cur.groups) && cur.groups.length ? (
        <NovaBracket tour={cur} />
      ) : isPcTeam && cur.matchesOpen === false ? (
        <Rv className="cs2-bracket-soon">
          <span><Ic name="layers" size={28} /></span>
          <h3>{lang === 'kz' ? 'Тор турнир кезінде пайда болады' : 'Сетка появится во время турнира'}</h3>
          <p>{lang === 'kz' ? 'Себу мен матч кестесі тіркелу аяқталғаннан кейін осында жарияланады.' : 'Распределение команд и расписание матчей опубликуем здесь после завершения регистрации.'}</p>
          <button onClick={() => goTab('teams')}>{lang === 'kz' ? 'Командаларды көру' : 'Смотреть команды'} <Ic name="chevR" size={17} /></button>
        </Rv>
      ) : <MlbbBracket M={M} userKey={userKey} onGoReg={() => goTab('reg')} matchesOpen={cur.matchesOpen !== false} tourId={cur.id} />)}
    </div>
  );
}
